import { useState, useEffect, useMemo } from 'react';

import { stripParentheses } from '../../../../../utils';

import {
  decisionTypes,
  balancedEquationAccountsTexting,
  thePrinciplesAccountsTexting,
  accountColumnTitles
 } from './data';

import InstructorAccountItemCheckbox from './accountCheckbox';
import InstructorAccountItemRadioButton from './accountRadioButton';
import DecisionTypeSelector from './decisionTypeSelector';

import './styles.css';

export default function InstructorLevelItemsModal({
  scenarioLevelNumber,
  onCloseButtonClick,
  onSaveButtonClick,
  isButtonLoading,
  gameType,
  filterItems,
  editingScenarioLevelPreCheckedItems = [],
  decisionType = '',
  businessType
}) {
  const getVisibleItemsByFilters = (selectedParent, selectedFilter) => {
    return filterItems[selectedParent][selectedFilter];
  };

  const defaultParent = Object.keys(filterItems)[0] || '';
  const defaultFilter = defaultParent ? Object.keys(filterItems[defaultParent])[0] : '';

  const [selectedFilterParent, setSelectedFilterParent] = useState(defaultParent);
  const [selectedFilter, setSelectedFilter] = useState(defaultFilter);
  const [accountItemsVisible, setAccountItemsVisible] = useState([]);
  const [accountItemsChecked, setAccountItemsChecked] = useState([]);
  const [isButtonVisible, setIsButtonVisible] = useState(false);
  
  const getDefaultDecisionType = () => {
    if (decisionType) return decisionType;
    
    if (gameType === 'Account Classification') {
      if (scenarioLevelNumber === 1) return 'class-elements';
      if (scenarioLevelNumber === 2) return 'class-default';
      return 'class-tricky';
    }
    
    if (gameType === 'Normal Balance') {
      if (scenarioLevelNumber === 1) return 'norm-elements';
      if (scenarioLevelNumber === 2) return 'norm-default';
      return 'norm-tricky';
    }
    
    if (gameType === 'Balanced Equation') {
      if (scenarioLevelNumber === 1) return 'increase-decrease';
      if (scenarioLevelNumber === 2) return 'debit-credit';
      return 'accounts-impacted';
    }
    
    if (gameType === 'The Principles') {
      // Only two Principles decision types are user-selectable now:
      // revenue-expense and assumptions-principles-constraints. Default
      // to revenue-expense regardless of level number.
      return 'revenue-expense';
    }
    
    return '';
  };

  const [selectedDecisionType, setSelectedDecisionType] = useState(getDefaultDecisionType);

  useEffect(() => {
    if (Object.keys(filterItems).length > 0) {
      setIsButtonVisible(true);
      const defaultParent = Object.keys(filterItems)[0];
      const defaultFilter = Object.keys(filterItems[defaultParent])[0] || '';
  
      setSelectedFilterParent(defaultParent);
      setSelectedFilter(defaultFilter);
  
      if (defaultParent && defaultFilter) {
        const visibleItems = getVisibleItemsByFilters(defaultParent, defaultFilter);
        setAccountItemsVisible(visibleItems);
  
        let preCheckedItems = [];

        if(!editingScenarioLevelPreCheckedItems.length) {
          preCheckedItems = Object.values(filterItems).flatMap(childFilters => Object.values(childFilters).flat());
        } else {
          preCheckedItems = Object.values(filterItems)
            .flatMap(childFilters => Object.values(childFilters).flat())
            .filter(item => editingScenarioLevelPreCheckedItems.some(preCheckedItem => preCheckedItem.text === item.text));
        }
    
        setAccountItemsChecked(preCheckedItems);
      }
    }
  }, [filterItems, scenarioLevelNumber]);

  // When the user picks a decision type, jump the filter tabs to the
  // matching top-level filterItems key. The new filterItems shape uses
  // decision-type labels (e.g. "Accounts", "Increase or Decrease",
  // "Assumptions, Principles, & Constraints") as top-level keys, so
  // looking up `decisionTypes[gameType][selectedDecisionType]` gives
  // the bucket to navigate into. Replaces the previous Corporate /
  // Non-Corporate / child-key-string-matching special cases.
  useEffect(() => {
    if (!selectedDecisionType) return;
    const label = decisionTypes?.[gameType]?.[selectedDecisionType];
    if (!label) return;
    const bucket = filterItems?.[label];
    if (!bucket) return;
    const firstChildKey = Object.keys(bucket)[0];
    if (!firstChildKey) return;
    setSelectedFilterParent(label);
    setSelectedFilter(firstChildKey);
    setAccountItemsVisible(bucket[firstChildKey] || []);
  }, [selectedDecisionType, filterItems, gameType]);

  const handleFilterChange = (name, parentName) => {
    let parentFilter = null;
  
    for (const [parentKey, childItems] of Object.entries(filterItems)) {
      if (parentKey === name || (Object.keys(childItems).includes(name) && parentKey === parentName)) {
        parentFilter = parentKey;
        break;
      }
    }
  
    const isParent = parentFilter === name;
    const newSelectedFilter = isParent
      ? Object.keys(filterItems[parentFilter])[0] || name
      : name;
  
    setSelectedFilter(newSelectedFilter);
    setSelectedFilterParent(parentFilter);
  
    setAccountItemsVisible(filterItems[parentFilter]?.[newSelectedFilter] || []);
  };
  

  const handleAccountItemCheck = (name, isChecked) => {
    let updatedCheckedItems;

    if (isChecked) {
      const item = accountItemsVisible.find(item => item.text === name);
      if (item) {
        updatedCheckedItems = [...accountItemsChecked, item];
      }
    } else {
      updatedCheckedItems = accountItemsChecked.filter(item => item.text !== name);
    }

    setAccountItemsChecked(updatedCheckedItems);
  };

  const handleAccountItemsSaved = () => {
    onCloseButtonClick();
  };

  const handleSaveButtonClick = () => {
    const accountItemsVisibleIds = accountItemsVisible.map(item => item.id);
    // const accountItemsCheckedToSave = editingScenarioLevelPreCheckedItems.length
    //   ? accountItemsChecked.filter((item, index, self) => index === self.findIndex(other => other.text === item.text))
    //   : accountItemsChecked.filter(item => accountItemsVisibleIds.includes(item.id));
    const accountItemsCheckedToSave = accountItemsChecked.filter(item => accountItemsVisibleIds.includes(item.id));

    onSaveButtonClick(accountItemsCheckedToSave, selectedFilter, selectedFilterParent, handleAccountItemsSaved, gameType, selectedDecisionType);
  };

  const selectAccountsHeadingPart = gameType === 'Balanced Equation' || gameType === 'The Principles'
    ? 'Selecting Transactions'
    : 'Select Accounts';
  
  const selectAccountsDescription =
    gameType === 'Balanced Equation'
      ? balancedEquationAccountsTexting[scenarioLevelNumber - 1] || balancedEquationAccountsTexting[2]
      : gameType === 'The Principles'
        ? thePrinciplesAccountsTexting
        : '';

  const handleDecisionTypeSelect = (value) => {
    setSelectedDecisionType(value);
  };

  // Show only the filter parent whose label matches the selected
  // decision type. Each top-level key in filterItems is a decision-
  // type label (e.g. "Accounts", "Deferral & Contra Account",
  // "Income Statement Impact"), so the active decision type's label
  // — looked up via decisionTypes[gameType][selectedDecisionType] —
  // identifies the single parent to render. The ISI special case
  // narrows children to those whose level name includes "income
  // statement impact"; non-ISI parents skip any such children.
  const filteredFilterItems = useMemo(() => {
    const matchingParent = decisionTypes?.[gameType]?.[selectedDecisionType];
    if (!matchingParent) return {};
    const childItems = filterItems?.[matchingParent];
    if (!childItems) return {};

    const isIsiSelected = selectedDecisionType === 'income-statement-impact';
    const filteredChildren = {};

    Object.entries(childItems).forEach(([childKey, items]) => {
      const childIsIsi = childKey.toLowerCase().includes('income statement impact');
      if (isIsiSelected && !childIsIsi) return;
      if (!isIsiSelected && childIsIsi) return;
      filteredChildren[childKey] = items;
    });

    return Object.keys(filteredChildren).length > 0
      ? { [matchingParent]: filteredChildren }
      : {};
  }, [filterItems, selectedDecisionType, gameType]);

  // Keep the currently selected filter valid as filteredFilterItems changes
  // (e.g. toggling to Classified & Multi-Step filters the sidebar down to
  // only design=['4'] rows; if nothing qualifies, blank the right panel).
  useEffect(() => {
    const parentKeys = Object.keys(filteredFilterItems);

    if (!parentKeys.length) {
      if (selectedFilterParent !== '' || selectedFilter !== '' || accountItemsVisible.length) {
        setSelectedFilterParent('');
        setSelectedFilter('');
        setAccountItemsVisible([]);
      }
      return;
    }

    const stillValid =
      selectedFilterParent &&
      filteredFilterItems[selectedFilterParent] &&
      filteredFilterItems[selectedFilterParent][selectedFilter];
    if (stillValid) return;

    const firstParent = parentKeys[0];
    const firstChild = Object.keys(filteredFilterItems[firstParent])[0];
    setSelectedFilterParent(firstParent);
    setSelectedFilter(firstChild);
    setAccountItemsVisible(filterItems[firstParent]?.[firstChild] || []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredFilterItems]);

  return (
    <div className={`modal-overlay`}>
      <div className="modal instructor-level-items-modal">
        <h3>Level {scenarioLevelNumber} | {selectAccountsHeadingPart}</h3>
        <button className="close-button" onClick={onCloseButtonClick}></button>

        <p>{selectAccountsDescription}</p>

        <div className="instructor-level-items-filter-container">
          <div className="instructor-level-items-filter-column" data-tour-id="level-filters-left">
            <h4>Select Filter</h4>
            {Object.entries(filteredFilterItems).map(([parentKey, childItems], index) => (
              <div key={`filter-${index}`}>
                <InstructorAccountItemRadioButton
                  id={`${parentKey}-${index}`}
                  isChecked={selectedFilterParent === parentKey}
                  label={parentKey}
                  onCheck={() => handleFilterChange(parentKey)}
                />
                <div className="account-item-children">
                  {Object.keys(childItems).map((childKey, subIndex) => (
                    <InstructorAccountItemRadioButton
                      key={`child-${index}-${subIndex}`}
                      id={`${childKey}-${index}-${subIndex}`}
                      isChecked={selectedFilterParent === parentKey && selectedFilter === childKey}
                      label={stripParentheses(childKey)}
                      onCheck={() => handleFilterChange(childKey, parentKey)}
                    />
                  ))}
                </div>
              </div>
            ))}
            { 
              Object.entries(filterItems).length > 0 && (
                <DecisionTypeSelector
                  gameType={gameType}
                  scenarioLevelNumber={scenarioLevelNumber}
                  decisionTypes={decisionTypes?.[gameType]}
                  editingScenarioLevelDecisionType={decisionType}
                  selectedDecisionType={selectedDecisionType}
                  onDecisionTypeSelect={handleDecisionTypeSelect}
                />
              )
            }
          </div>

          <div className="instructor-level-items-account-columns" data-tour-id="level-accounts-right">
            {(accountColumnTitles[gameType] || []).map((columnTitle, index) => {
              const relatedItems = accountItemsVisible?.filter((item) =>
                item.column === columnTitle
              );

              return (
                <div className="instructor-level-items-filter-column" key={`filter-column-${index}`}>
                  <h4>{columnTitle}</h4>

                  {relatedItems?.map((item, itemIndex) => (
                    <InstructorAccountItemCheckbox
                      key={`item-${index}-${itemIndex}`}
                      isChecked={accountItemsChecked.some(checkedItem => checkedItem.id === item.id)}
                      label={item.text}
                      onCheck={(event) =>
                        handleAccountItemCheck(item.text, event.target.checked)
                      }
                    />
                  ))}
                </div>
              );
            })}
          </div>
        </div>

        <div className="save-account-items-selection-container">
          {
            isButtonVisible &&
              <button
                className={`button-blue save-account-items-selection button-save-assignment ${isButtonLoading ? 'loading' : ''}`}
                data-tour-id="level-save-button"
                onClick={handleSaveButtonClick}
              >
                {!isButtonLoading && 'Save'}
              </button>
          }
        </div>
      </div>
    </div>
  );
}
